/**
 * ACRES User — Overview tab (balance, redeem CTA, achievements summary)
 */
import React, { useEffect, useState } from 'react';
import { acres } from '../../../lib/services/acresService';

interface Props { user?: any; onJumpToTasks: () => void; }

export const AcresOverview: React.FC<Props> = ({ onJumpToTasks }) => {
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [redeemPoints, setRedeemPoints] = useState<number>(0);
  const [redeeming, setRedeeming] = useState(false);
  const [preview, setPreview] = useState<any>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try { setSummary(await acres.getSummary()); }
    catch (e: any) { setError(e?.message || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    let cancel = false;
    if (redeemPoints && redeemPoints > 0) {
      acres.previewRedeem(redeemPoints).then(p => { if (!cancel) setPreview(p); }).catch(() => setPreview(null));
    } else { setPreview(null); }
    return () => { cancel = true; };
  }, [redeemPoints]);

  const doRedeem = async () => {
    if (!redeemPoints || redeemPoints <= 0) return;
    if (redeemPoints > (summary?.points_balance || 0)) { alert('Insufficient points'); return; }
    if (preview?.would_exceed_monthly_cap) { alert('This redemption would exceed the monthly system-wide cap. Try a smaller amount or try again next month.'); return; }
    if (!confirm(`Convert ${redeemPoints.toLocaleString()} points → ${preview?.shares_to_mint || (redeemPoints * 0.0001).toFixed(4)} fractional shares?\n\nThis is FINAL and immediately allocates Aureus shares from the current share phase.`)) return;
    setRedeeming(true);
    try {
      const idemKey = `redeem_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      await acres.redeem(redeemPoints, idemKey);
      setRedeemPoints(0); setPreview(null);
      await load();
      alert('✅ Shares minted successfully. Check your portfolio.');
    } catch (e: any) { alert('Redemption failed: ' + (e?.message || 'unknown')); }
    finally { setRedeeming(false); }
  };

  if (loading) return <div style={{ color: '#9CA3AF' }}>Loading…</div>;
  if (error) return <div style={errBox}>{error}</div>;

  const bal = Number(summary?.points_balance || 0);
  const sharesEq = bal * 0.0001;
  const drawEntries = summary?.draw_entries_this_month || 0;
  const monthCapPct = Number(summary?.monthly_cap_used_pct || 0);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
        <Stat label="Points Balance" value={bal.toLocaleString()} accent="#F59E0B" />
        <Stat label="Share Equivalent" value={sharesEq.toFixed(4)} accent="#10B981" />
        <Stat label="Draw Entries (this month)" value={drawEntries} accent="#60A5FA" />
        <Stat label="Tasks Completed" value={summary?.tasks_approved_total || 0} />
        <Stat label="Your Rank" value={summary?.rank ? `#${summary.rank}` : '—'} />
        <Stat label="Lifetime Earned" value={Number(summary?.lifetime_points_earned || 0).toLocaleString()} />
      </div>

      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>💰 Redeem Points → Fractional Shares</div>
            <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>1,000 points = 0.1 share · System-wide monthly cap: 10,000 shares ({monthCapPct.toFixed(1)}% used)</div>
          </div>
          <button onClick={onJumpToTasks} style={btnGhost}>+ Earn More Points</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'end' }}>
          <div>
            <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 4 }}>Points to redeem</div>
            <input type="number" min={0} max={bal} value={redeemPoints || ''} onChange={e => setRedeemPoints(Number(e.target.value) || 0)} style={input} />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 4 }}>You will receive</div>
            <div style={{ ...input, background: '#0A0A0A' }}>{(preview?.shares_to_mint ?? (redeemPoints * 0.0001)).toFixed?.(4) || '0.0000'} shares</div>
          </div>
          <button onClick={doRedeem} disabled={redeeming || !redeemPoints || redeemPoints > bal || preview?.would_exceed_monthly_cap} style={{
            ...btnPrimary,
            opacity: (redeeming || !redeemPoints || redeemPoints > bal || preview?.would_exceed_monthly_cap) ? 0.5 : 1
          }}>{redeeming ? 'Redeeming…' : 'Redeem'}</button>
        </div>
        {preview?.would_exceed_monthly_cap && (
          <div style={warn}>⚠ This redemption would exceed the system-wide monthly share cap. Try a smaller amount.</div>
        )}
        <div style={{ fontSize: 11, color: '#6B7280', marginTop: 8 }}>Shares are minted from the current share phase pool and recorded on the immutable points ledger. This action is final.</div>
      </div>

      <div style={{ ...card, marginTop: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>🏆 Achievements</div>
        {Array.isArray(summary?.achievements) && summary.achievements.length ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
            {summary.achievements.map((a: any) => (
              <div key={a.id || a.tier_key} style={{ padding: 10, background: '#0A0A0A', border: '1px solid #1E1E1E', borderRadius: 8, opacity: a.unlocked ? 1 : 0.5 }}>
                <div style={{ fontSize: 22 }}>{a.icon || '🥇'}</div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{a.name}</div>
                <div style={{ fontSize: 11, color: '#9CA3AF' }}>{a.unlocked ? `Unlocked ${a.unlocked_at ? new Date(a.unlocked_at).toLocaleDateString() : ''}` : `${a.progress_pct ?? 0}% to unlock`}</div>
                {!a.unlocked && (
                  <div style={{ height: 4, background: '#1E1E1E', borderRadius: 2, marginTop: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.min(100, a.progress_pct || 0)}%`, height: '100%', background: '#F59E0B' }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : <div style={{ color: '#9CA3AF', fontSize: 13 }}>No achievements yet — start earning points to unlock badges.</div>}
      </div>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: any; accent?: string }> = ({ label, value, accent }) => (
  <div style={card}>
    <div style={{ fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 700, color: accent || '#FFFFFF', marginTop: 4 }}>{value}</div>
  </div>
);
const card: React.CSSProperties = { background: '#0F0F0F', border: '1px solid #1E1E1E', borderRadius: 8, padding: 14 };
const input: React.CSSProperties = { width: '100%', padding: '10px 12px', background: '#0F0F0F', border: '1px solid #2A2A2A', borderRadius: 6, color: '#FFF', fontSize: 14 };
const btnPrimary: React.CSSProperties = { padding: '10px 20px', background: '#F59E0B', color: '#000', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 14 };
const btnGhost: React.CSSProperties = { padding: '8px 14px', background: 'transparent', color: '#E5E7EB', border: '1px solid #374151', borderRadius: 6, cursor: 'pointer', fontSize: 13 };
const errBox: React.CSSProperties = { padding: 10, background: 'rgba(220,38,38,0.1)', border: '1px solid #DC2626', borderRadius: 6, color: '#FCA5A5' };
const warn: React.CSSProperties = { padding: 8, background: 'rgba(245,158,11,0.1)', border: '1px solid #F59E0B', borderRadius: 6, color: '#FBBF24', fontSize: 12, marginTop: 8 };
